import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { NUDGE_CARD_LEAD_IN_MS } from '@/components/atoms';
import { TIP_DISMISSAL_LIMIT, TIP_PHONE_OPEN_DEPTH, TIP_START_PX } from '@/config/pageTips';
import { type ResolvedPageTip, resolvePageTip } from '@/utils/pageTip';

/**
 * The pages this visitor has closed a tip on, comma-joined.
 *
 * Pages and not messages, which was the whole of the 2026-10-25 change:
 * closing the card on the home page used to retire that *message*, and because
 * the cost tip also served the listings and both calculators, one dismissal
 * left four pages silent. A visitor reads that as the site having stopped
 * working. Now a close means "not opened by itself on this page", and nowhere
 * else — and since 2026-10-25 it does not even mean gone, because the launcher
 * stays behind.
 */
export const TIP_DISMISSED_KEY = 'kbx-tips-dismissed';
/** How many they have closed without acting on one. See TIP_DISMISSAL_LIMIT. */
export const TIP_DISMISSAL_RUN_KEY = 'kbx-tips-closed-run';

/**
 * `hidden`   — not yet. The state every page view starts in.
 * `launcher` — the disc, pinging, with the card a beat away.
 * `peek`     — the phone's collapsed offer. Never reached on a pointer device.
 * `open`     — the card, with the whole message in it.
 * `quiet`    — the disc, done asking: no ping, no second opening, still
 *              clickable for the rest of the session.
 *
 * `launcher` and `quiet` paint the same object and differ only in whether it
 * is still trying to be noticed. They are two stages rather than one stage and
 * a flag because that is the actual difference between a tip on its way in and
 * a tip that has been dealt with, and every transition in this hook is easier
 * to read for naming it.
 */
export type TipStage = 'hidden' | 'launcher' | 'peek' | 'open' | 'quiet';

export type ScrollNudge = {
  /** The tip this page would show, or null if it shows none. */
  tip: ResolvedPageTip | null;
  stage: TipStage;
  /**
   * The visitor asked for the card — tapped the pill or the launcher. Always
   * visitor-initiated, which is what decides whether the card takes focus;
   * see the component.
   */
  open: () => void;
  /** The visitor closed it, or pressed Escape. */
  dismiss: () => void;
  /** The visitor took the tip up — pressed the CTA or the WhatsApp button. */
  engage: () => void;
};

/**
 * Below the theme's `md`, where the card is a sheet rather than a corner card
 * and the sticky action bar is on screen.
 *
 * A `matchMedia` call and not `useMediaQuery`, and the distinction matters.
 * The rule this site works under — every breakpoint is CSS, never a hook — is
 * there because the pages are prerendered, so a width-dependent *render* would
 * either mismatch the HTML or flash. This is not a render: it is read inside
 * the scroll handler, long after hydration, to decide which of two thresholds
 * applies. Nothing about the first paint depends on it, and there is no server
 * render for it to disagree with.
 *
 * 899.95px is MUI's own `down('md')`, written out because this is plain DOM
 * rather than an `sx` prop with a theme behind it.
 */
const PHONE_QUERY = '(max-width: 899.95px)';

/**
 * `sessionStorage` throws outright in some privacy modes, so every access is
 * wrapped and every failure is treated as "nothing remembered". A visitor who
 * gets shown a tip twice is a far better failure than a page that does not
 * render — the same bargain `useImportPromiseBanner` makes.
 */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage is blocked. The tip still closes for this page view; it may come
    // back on the next one.
  }
}

const dismissedPages = (): string[] =>
  (readSession(TIP_DISMISSED_KEY) ?? '').split(',').filter(Boolean);

const dismissalRun = (): number =>
  Number.parseInt(readSession(TIP_DISMISSAL_RUN_KEY) ?? '0', 10) || 0;

/**
 * Whether a card may still open itself this session — which since the launcher
 * exists is a much smaller claim than it used to be. Nothing here hides a tip;
 * it only stops one arriving unasked.
 */
function autoOpenIsSilenced(): boolean {
  return dismissalRun() >= TIP_DISMISSAL_LIMIT;
}

/**
 * Record that this page's tip has been dealt with.
 *
 * The run counter is incremented **only the first time** a given page is
 * closed. Without that guard, a visitor who pulled a tip back up from the
 * launcher and closed it again would be charged twice for reading the same
 * message — and three such re-reads would silence the auto-open for the
 * session. Pressing a button that says "hide this tip" on a tip you asked to
 * see is not the same signal as closing one that arrived on its own.
 */
function rememberDismissal(key: string): void {
  const seen = dismissedPages();
  if (seen.includes(key)) return;
  writeSession(TIP_DISMISSED_KEY, [...seen, key].join(','));
  writeSession(TIP_DISMISSAL_RUN_KEY, String(dismissalRun() + 1));
}

/**
 * The scroll tip for this page: which stage it is in, and what each of the
 * visitor's three possible answers means.
 *
 * ## The shape of a page view
 *
 * Nothing at all until the visitor has travelled `TIP_START_PX`. Then:
 *
 * - **pointer device** → `launcher`, and `NUDGE_CARD_LEAD_IN_MS` later `open`.
 *   The two beats are the point: the disc pings into the corner first so the
 *   card opens somewhere the eye has already been sent. The card used to
 *   simply appear there, frosted and tinted to match its surroundings, and the
 *   complaint that started this rework was that nobody noticed it had.
 * - **phone** → `peek`, the collapsed offer, which opens on a tap or once the
 *   visitor has passed `TIP_PHONE_OPEN_DEPTH` and plainly stayed.
 * - **either, if the page's tip has already been dealt with this session, or
 *   if two tips have been closed in a row** → straight to `quiet`. The
 *   launcher and nothing else: the information stays one click away, and
 *   nothing opens itself.
 *
 * And before any of that, if the page view *begins* already past the threshold
 * — a reload whose scroll position was restored, or a `#fragment` landing —
 * the disc goes up immediately in its `quiet` state. Only the disc, and it
 * does not ping; everything above still has to be earned by real travel.
 *
 * Whatever happens next, the end state is `quiet` rather than `hidden`. A
 * dismissal collapses the card back into the disc it grew out of, and acting
 * on the tip does the same. That is what makes the card's new assertiveness
 * acceptable — closing it costs the visitor nothing, because nothing is lost.
 *
 * ## Why this one does not need the ribbon's pre-paint script
 *
 * `useImportPromiseBanner` goes to some lengths — an attribute on `<html>` set
 * before the first frame, and visibility decided in CSS — because the import
 * ribbon is *in* the prerendered HTML, so a React state seeded from
 * `sessionStorage` would have to start at "visible" to match the server and
 * then flip after hydration.
 *
 * None of that applies here, and the reason is the trigger. Nothing in this
 * feature has appeared yet at first paint on any page, in any session: the
 * stage starts `hidden`, the prerendered HTML contains no tip markup of any
 * kind, and nothing can move it on until a real scroll event has happened —
 * which is long after hydration. The storage read happens inside that handler,
 * where it cannot be part of a first render that has to match the server's.
 * There is nothing to flash and nothing to mismatch, so the simple thing is
 * also the correct one.
 *
 * The disc may now go up on arrival when the page view starts already deep
 * (see `arrivedDeep`), and that does not disturb any of the above: it happens
 * in this effect, after hydration, on a client that has a scroll position to
 * read. A prerendered page is rendered at the top of the document with no
 * effects run at all, so the built HTML still contains no tip markup.
 *
 * ## The trigger is a scroll listener, not an observer
 *
 * `useInView` would be the house answer, but it watches an *element*, and
 * there is no element at a given depth of a page to watch — the tip is mounted
 * by `SiteLayout`, outside whatever the route happens to render. A sentinel
 * absolutely positioned at 55% of the document would be a fiction that breaks
 * the moment a band above it loads an image and changes height.
 *
 * So: a passive listener, throttled to one measurement per frame, removed the
 * instant it has fired. Nothing stays subscribed to scroll after it has done
 * its job, which is the part of `useInView`'s design that actually matters.
 *
 * It deliberately does **not** measure on mount, so a visitor restored to the
 * middle of a page by `ScrollRestoration`, or arriving on a `#fragment`, is
 * not treated as having scrolled: a *card* in the first frame on arrival is
 * precisely the intrusive interstitial the threshold exists to avoid. The card
 * has to be scrolled into, not landed on. The disc is the one exception, and a
 * narrow one — see `arrivedDeep`.
 */
export function useScrollNudge(): ScrollNudge {
  const { pathname } = useLocation();
  const tip = useMemo(() => resolvePageTip(pathname), [pathname]);
  const [stage, setStage] = useState<TipStage>('hidden');

  /**
   * Set once the scroll listener has nothing left to deliver on this page
   * view, so a visitor who scrolls back up and down again is not nudged twice.
   * It is not set by reaching `peek`: a phone that has only shown the
   * collapsed offer is still owed the card, and the listener stays attached to
   * deliver it.
   */
  const spent = useRef(false);

  /**
   * The pending `launcher` → `open` handoff.
   *
   * It has to be cancellable from three places — a route change, an unmount,
   * and a visitor who answers the launcher before the beat is up — and all
   * three would otherwise land a `setStage('open')` on a tip that is no longer
   * the one on screen.
   */
  const leadIn = useRef(0);
  const clearLeadIn = useCallback(() => {
    if (!leadIn.current) return;
    window.clearTimeout(leadIn.current);
    leadIn.current = 0;
  }, []);

  useEffect(() => {
    // A new page: the previous page's tip goes, and this one is re-armed.
    setStage('hidden');
    spent.current = false;
    clearLeadIn();

    if (!tip) return;

    /**
     * Whether this page view may only ever offer the launcher.
     *
     * Read once, here, rather than at the moment the threshold is crossed:
     * both halves of it are session facts that cannot change while the visitor
     * stays on one page, since the only things that write them are this page's
     * own handlers — and those all end the scroll listener's job anyway.
     */
    const quietOnly = autoOpenIsSilenced() || dismissedPages().includes(tip.key);

    let frame = 0;

    /**
     * Where this page view began, which is what `TIP_START_PX` is measured
     * from — not from the top of the document.
     *
     * The two are the same number on an ordinary load and very different ones
     * when something has already moved the page: `ScrollRestoration` putting a
     * reloaded page back where it was, or a `#fragment` jumping to a band
     * halfway down. Both of those fire real scroll events, and against an
     * absolute threshold both would count as "this visitor has scrolled" when
     * the visitor has done nothing at all.
     *
     * Read here rather than at the first scroll event because this effect is a
     * passive one: React Router has already restored the position in a layout
     * effect by the time it runs.
     *
     * The comparison below is on the *distance* from it, not the direction. A
     * visitor who lands halfway down a restored page and scrolls back up has
     * engaged with it every bit as much as one who scrolled down — more so, if
     * anything, since going back up is what people do when they are looking
     * for something they passed. Measuring the gap in one direction only would
     * leave that reader alone for as long as they stayed above where they
     * started.
     */
    const startY = window.scrollY;

    /**
     * The page view began already as deep as a tip would have asked for —
     * a reload that `ScrollRestoration` put back, or a `#fragment` landing.
     *
     * The disc goes up straight away in that case, and **only the disc**. The
     * threshold's job is to stop a card arriving over content nobody asked to
     * have covered, and that argument is untouched here: nothing opens, the
     * disc does not even ping, and the card still has to be genuinely scrolled
     * into before it will open itself.
     *
     * What this fixes is the opposite failure, which is easy to miss and reads
     * far worse. Reload a page and the position is restored, so `startY` is
     * already deep — and a visitor who then reads from where they left off
     * without travelling another 240px got *nothing at all*: no card, and no
     * sign the feature existed. The threshold was quietly withholding the one
     * piece of chrome that is allowed to arrive unasked, because a 48px disc in
     * the corner is not an interstitial by any reading.
     */
    const arrivedDeep = startY >= TIP_START_PX;
    if (arrivedDeep) setStage('quiet');

    /**
     * Nothing is left for the listener to do: this page may only ever show the
     * disc, and the disc is already up. The other three combinations all still
     * have something owed — a quieted page that has to wait for the threshold
     * before its disc appears, or an armed page that owes a card.
     */
    if (quietOnly && arrivedDeep) return;

    /** The listener has delivered everything this page view had for it. */
    const finish = () => {
      spent.current = true;
      window.removeEventListener('scroll', onScroll);
    };

    const measure = () => {
      frame = 0;
      if (spent.current) return;
      if (Math.abs(window.scrollY - startY) < TIP_START_PX) return;

      // Seen already, or two closes deep: the disc, and only on request.
      if (quietOnly) {
        finish();
        setStage('quiet');
        return;
      }

      // A pointer device gets the launcher now and the card a beat later. A
      // phone gets the collapsed offer now and the card once it has been asked
      // for or earned — the entire difference between the two, and the reason
      // the query is read here rather than at render.
      if (!window.matchMedia(PHONE_QUERY).matches) {
        finish();
        setStage('launcher');
        leadIn.current = window.setTimeout(() => {
          leadIn.current = 0;
          setStage('open');
        }, NUDGE_CARD_LEAD_IN_MS);
        return;
      }

      setStage('peek');

      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0 || window.scrollY / scrollable < TIP_PHONE_OPEN_DEPTH) return;

      finish();
      setStage('open');
    };

    // One measurement per frame. A scroll listener that reads `scrollHeight`
    // on every event reads layout dozens of times per flick, and this one is
    // attached on every page of the site.
    const onScroll = () => {
      if (frame || spent.current) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      clearLeadIn();
    };
  }, [tip, clearLeadIn]);

  /**
   * The visitor asked for the card — from the phone's collapsed offer, or from
   * the launcher at either of its two stages.
   *
   * It cancels the pending lead-in, which matters for the one case where both
   * could fire: a visitor who clicks the launcher inside the 700ms before the
   * card was going to open anyway. Without the cancel the timer would land a
   * second `setStage('open')` on an already-open card — harmless today, and
   * exactly the kind of thing that stops being harmless when somebody later
   * hangs an entrance or an analytics call off that transition.
   */
  const open = useCallback(() => {
    clearLeadIn();
    spent.current = true;
    setStage('open');
  }, [clearLeadIn]);

  /**
   * Closing a tip collapses it into the launcher, quiets this page for the
   * session, and counts against the run. Two in a row and nothing opens itself
   * for the rest of the session — but the launcher still appears on every
   * tipped page, and the counter resets when the tab closes. The tips carry
   * information worth having, so nothing in this feature silences them
   * permanently and nothing in it writes to `localStorage`.
   */
  const dismiss = useCallback(() => {
    if (!tip) return;
    clearLeadIn();
    spent.current = true;
    setStage('quiet');
    rememberDismissal(tip.key);
  }, [tip, clearLeadIn]);

  /**
   * Acting on a tip retires it exactly as closing it does — to the launcher,
   * on this page, for this session. The visitor has the message and does not
   * need it again when they come back here. It does **not** reach any other
   * page: a visitor who found one tip useful is the last one to start hiding
   * the rest from.
   *
   * The difference from `dismiss` is the run counter, which this clears.
   * Somebody who acts on a tip has not told us to stop.
   */
  const engage = useCallback(() => {
    if (!tip) return;
    clearLeadIn();
    spent.current = true;
    setStage('quiet');
    const seen = dismissedPages();
    if (!seen.includes(tip.key)) writeSession(TIP_DISMISSED_KEY, [...seen, tip.key].join(','));
    writeSession(TIP_DISMISSAL_RUN_KEY, '0');
  }, [tip, clearLeadIn]);

  /**
   * Escape closes it, which is the one keyboard affordance a non-modal dialog
   * still owes the visitor. On `document` rather than on the card, because the
   * card does not take focus when it opens by itself (see the component): a
   * keyboard visitor is still wherever they were in the page, and a handler on
   * the card would only fire if they had already tabbed into it.
   *
   * Only while something is actually open or offered. At `launcher` the card
   * is still 700ms away and at `quiet` there is nothing but a disc, and in
   * both of those an Escape is far more likely to be aimed at something else
   * on the page — a menu, a lightbox, the browser's own find bar — than at a
   * 48px control the visitor may not have looked at yet. Swallowing it there
   * would mean a keypress silently retiring a tip that had not been read.
   */
  useEffect(() => {
    if (stage !== 'open' && stage !== 'peek') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [stage, dismiss]);

  return { tip, stage, open, dismiss, engage };
}
